import { Card, Table, Row, Col, Select, DatePicker, Button, Statistic, Tag, Space } from 'antd';
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { checkupApi } from '../../../api/checkup.api';
import { eventApi } from '../../../api/event.api';
import { schoolApi } from '../../../api/school.api';
import PageHeader from '../../../components/common/PageHeader';
import { formatDate } from '../../../utils/helpers';

const { RangePicker } = DatePicker;

const ReportPage = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({ page: 1, limit: 50 });

  const { data: events } = useQuery({
    queryKey: ['events-report'],
    queryFn: () => eventApi.getAll({ limit: 100 }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['event-stats', selectedEvent],
    queryFn: () => checkupApi.getEventStats(selectedEvent),
    enabled: !!selectedEvent,
  });

  const { data: checkupsData } = useQuery({
    queryKey: ['checkups-report', filters],
    queryFn: () => checkupApi.getAll({ ...filters, event: selectedEvent }),
    enabled: !!selectedEvent,
  });

  const stats = statsData?.data || {};
  const checkups = checkupsData?.data || [];
  const meta = checkupsData?.meta || {};

  const columns = [
    { title: 'Child', key: 'child', render: (_, r) => r.child?.name || '-' },
    { title: 'Age', key: 'age', render: (_, r) => r.child?.age ? `${r.child.age} yrs` : '-' },
    { title: 'Oral Hygiene', dataIndex: 'oralHygieneStatus', key: 'oral', render: (v) => <Tag>{v?.toUpperCase()}</Tag> },
    { title: 'Cavities', dataIndex: 'hasCavities', key: 'cavities', render: (v) => v ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag> },
    { title: 'Gum Issues', dataIndex: 'hasGumIssues', key: 'gum', render: (v) => v ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag> },
    { title: 'Treatment', dataIndex: 'treatmentRequired', key: 'treatment', render: (v) => v ? <Tag color="orange">Required</Tag> : <Tag color="green">Not Required</Tag> },
    { title: 'Treatment Type', dataIndex: 'treatmentType', key: 'ttype', render: (v) => v || '-' },
    { title: 'Doctor', key: 'doctor', render: (_, r) => r.doctor?.name || '-' },
    { title: 'Remarks', dataIndex: 'doctorRemarks', key: 'remarks', ellipsis: true, render: (v) => v || '-' },
    { title: 'Next Visit', dataIndex: 'nextVisitMonths', key: 'next', render: (v) => v ? `${v} months` : '-' },
    { title: 'Date', dataIndex: 'checkupDate', key: 'date', render: (v) => formatDate(v) },
  ];

  return (
    <div>
      <PageHeader title="Event Reports" subtitle="View and export dental camp event reports" />

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 320 }}
            placeholder="Select an event to view report"
            onChange={setSelectedEvent}
            showSearch
            optionFilterProp="children"
          >
            {(events?.data || []).map((e) => (
              <Select.Option key={e.id} value={e.id}>{e.title} — {formatDate(e.eventDate)}</Select.Option>
            ))}
          </Select>
          <Button icon={<DownloadOutlined />} disabled={!selectedEvent}>Export Report</Button>
        </Space>
      </Card>

      {selectedEvent && stats.totalChecked !== undefined && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Checked" value={stats.totalChecked || 0} valueStyle={{ color: 'var(--wl-primary)' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Treatment Required" value={stats.treatmentRequired || 0} valueStyle={{ color: '#f5222d' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Cavities Found" value={stats.hasCavities || 0} valueStyle={{ color: '#fa8c16' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Gum Issues" value={stats.hasGumIssues || 0} valueStyle={{ color: '#722ed1' }} />
            </Card>
          </Col>
        </Row>
      )}

      {selectedEvent && (
        <Card title="Detailed Checkup Report">
          <Table
            columns={columns}
            dataSource={checkups}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            size="small"
            pagination={{
              current: meta.page,
              pageSize: meta.limit,
              total: meta.total,
              onChange: (page, limit) => setFilters((p) => ({ ...p, page, limit })),
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`,
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default ReportPage;
